/*
  Selektoren für das Monatsraster.
*/

import { parseDateValue, toIsoDate } from "../date/month";
import {
  CUSTOM_DEPARTMENT_ROW_ID,
  CUSTOM_DEPARTMENT_ROW_LABEL,
  isCustomDepartmentEntry,
} from "./departments";

function includesQuery(value, query) {
  return String(value || "").toLocaleLowerCase("de").includes(query);
}

function toStartOfDay(value) {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function buildDayKeysBetween(startValue, endValue) {
  const start = toStartOfDay(startValue);
  const rawEnd = toStartOfDay(endValue || startValue);
  if (!start) return [];

  const end = rawEnd && rawEnd >= start ? rawEnd : start;
  const keys = [];

  for (
    let cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    cursor <= end;
    cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1)
  ) {
    keys.push(toIsoDate(cursor));
  }

  return keys;
}

function normalizeDepartmentName(value) {
  const label = String(value || "").trim();
  return label || "Ohne Abteilung";
}

function getDepartmentItemName(item) {
  if (Array.isArray(item)) {
    return normalizeDepartmentName(item[1] ?? item[0]);
  }

  if (item && typeof item === "object") {
    return normalizeDepartmentName(item.name ?? item.label ?? item.department ?? item.title ?? item.id);
  }

  return normalizeDepartmentName(item);
}

function getDepartmentItemId(item) {
  if (Array.isArray(item)) return String(item[0] ?? "").trim();
  if (item && typeof item === "object") return String(item.id ?? item.DepartmentID ?? item.departmentId ?? "").trim();
  return "";
}

function getDepartmentItemShortName(item) {
  if (Array.isArray(item)) return String(item[2] ?? "").trim();
  if (item && typeof item === "object") {
    return String(item.shortName ?? item.shorthand_symbol ?? item.short_name ?? "").trim();
  }
  return "";
}

export function getDepartmentRowId(value) {
  return normalizeDepartmentName(value);
}

/*
  Individuelle Abteilungen landen alle in derselben Zeile. Ihr Name gehört zu
  genau einem Eintrag; je Name eine Zeile hiesse, das Raster wächst mit jedem
  Sonderfall um eine fast leere Zeile.
*/
export function getShiftDepartmentRowId(shift) {
  if (isCustomDepartmentEntry(shift)) return CUSTOM_DEPARTMENT_ROW_ID;
  return getDepartmentRowId(shift?.department);
}

function getShiftRowId(shift, rowMode = "employee") {
  return rowMode === "department" ? getShiftDepartmentRowId(shift) : String(shift?.employeeId || "");
}

export function getShiftDayKeys(shift) {
  if (shift?.entryType === "absence") {
    return buildDayKeysBetween(shift?.start || shift?.dateKey, shift?.end || shift?.dateKey);
  }

  const dateKey = String(shift?.dateKey || toIsoDate(shift?.start) || "").trim();
  return dateKey ? [dateKey] : [];
}

function formatShiftCount(value) {
  const count = Math.max(0, Number(value || 0));
  return count === 1 ? "1 Einsatz" : `${count} Einsätze`;
}

function buildOrderIndexMap(order) {
  return new Map((Array.isArray(order) ? order : []).map((id, index) => [String(id), index]));
}

/*
  Reihenfolge der Kacheln innerhalb einer Tageszelle: nach Startzeit, bei
  gleicher Zeit nach Name der Person.

  Das ist nicht kosmetisch. Eine Zelle zeigt nur die ersten drei Einträge, den
  Rest verbirgt der "+N"-Knopf — ohne feste Ordnung entschiede die Reihenfolge
  der API, wen man überhaupt zu sehen bekommt, und sie könnte sich beim
  Neuladen ändern. Im Abteilungs-Modus, wo eine Zelle das ganze Team eines
  Tages enthält, wäre das der Unterschied zwischen Plan und Zufall.
*/
function compareShiftsInCell(left, right) {
  const leftStart = String(left?.start || "");
  const rightStart = String(right?.start || "");
  if (leftStart !== rightStart) return leftStart < rightStart ? -1 : 1;

  return String(left?.employeeName || "").localeCompare(String(right?.employeeName || ""), "de", {
    sensitivity: "base",
  });
}

export function groupShiftsByCell(shifts, rowMode = "employee") {
  const grouped = (Array.isArray(shifts) ? shifts : []).reduce((accumulator, shift) => {
    const rowId = getShiftRowId(shift, rowMode);
    if (!rowId) return accumulator;

    getShiftDayKeys(shift).forEach((dateKey) => {
      const key = `${rowId}::${dateKey}`;
      if (!accumulator[key]) accumulator[key] = [];
      accumulator[key].push(shift);
    });

    return accumulator;
  }, {});

  Object.values(grouped).forEach((cellShifts) => cellShifts.sort(compareShiftsInCell));
  return grouped;
}

export function filterShiftsByDays(shifts, days) {
  const visibleDays = new Set((Array.isArray(days) ? days : []).map((day) => day.key));
  return (Array.isArray(shifts) ? shifts : []).filter((shift) =>
    getShiftDayKeys(shift).some((dateKey) => visibleDays.has(dateKey))
  );
}

export function groupPublicHolidayNamesByDate(publicHolidays) {
  return (Array.isArray(publicHolidays) ? publicHolidays : []).reduce((accumulator, holiday) => {
    const dateKey = String(holiday?.date || "").slice(0, 10);
    const name = String(holiday?.name || "").trim();
    if (!dateKey || !name) return accumulator;

    if (!accumulator[dateKey]) accumulator[dateKey] = [];
    if (!accumulator[dateKey].includes(name)) accumulator[dateKey].push(name);
    return accumulator;
  }, {});
}

export function buildVisibleEmployees({ employees, shifts, query = "", sortDirection = "asc" }) {
  const normalizedQuery = String(query || "").trim().toLocaleLowerCase("de");
  const shiftsByEmployeeId = (Array.isArray(shifts) ? shifts : []).reduce((accumulator, shift) => {
    if (!accumulator[shift.employeeId]) accumulator[shift.employeeId] = [];
    accumulator[shift.employeeId].push(shift);
    return accumulator;
  }, {});

  const filtered = (Array.isArray(employees) ? employees : []).filter((employee) => {
    if (!normalizedQuery) return true;

    const employeeMatches =
      includesQuery(employee.name, normalizedQuery) ||
      includesQuery(employee.email, normalizedQuery);

    if (employeeMatches) return true;

    return (shiftsByEmployeeId[employee.id] || []).some(
      (shift) =>
        includesQuery(shift.title, normalizedQuery) ||
        includesQuery(shift.department, normalizedQuery) ||
        includesQuery(shift.timeLabel, normalizedQuery)
    );
  });

  return sortDirection === "desc" ? [...filtered].reverse() : filtered;
}

/*
  Bringt Einträge in die Reihenfolge einer Id-Liste – die Reihenfolge aus den
  Einstellungen, die sofort im Raster stehen soll, ohne den Plan neu zu laden.
  Was in der Liste fehlt, folgt dahinter in seiner bisherigen Reihenfolge.
*/
export function sortByIdOrder(items, orderedIds, getId = (item) => item?.id) {
  const positionById = buildOrderIndexMap(orderedIds);
  const positionOf = (item) => {
    const id = String(getId(item) ?? "");
    return positionById.has(id) ? positionById.get(id) : Number.POSITIVE_INFINITY;
  };

  return (Array.isArray(items) ? items : [])
    .map((item, index) => ({ item, index, position: positionOf(item) }))
    .sort((left, right) => {
      if (left.position !== right.position) return left.position < right.position ? -1 : 1;
      return left.index - right.index;
    })
    .map(({ item }) => item);
}

// Abteilungen kommen als Objekt oder als Zeile ([id, name, ...]) daher.
export function sortDepartmentsByIds(departments, orderedIds) {
  return sortByIdOrder(departments, orderedIds, getDepartmentItemId);
}

export function buildVisibleDepartments({
  departments,
  shifts,
  query = "",
  sortDirection = "asc",
  departmentOrder = [],
  includeUnassigned = false,
}) {
  const normalizedQuery = String(query || "").trim().toLocaleLowerCase("de");
  const orderIndexById = buildOrderIndexMap(departmentOrder);
  const rowsById = new Map();

  (Array.isArray(departments) ? departments : []).forEach((item) => {
    const name = getDepartmentItemName(item);
    const id = getDepartmentRowId(name);
    if (!id || rowsById.has(id)) return;
    rowsById.set(id, {
      id,
      departmentId: getDepartmentItemId(item),
      name,
      shortName: getDepartmentItemShortName(item),
      shifts: [],
      employeeIds: new Set(),
    });
  });

  (Array.isArray(shifts) ? shifts : []).forEach((shift) => {
    const id = getShiftDepartmentRowId(shift);
    if (!rowsById.has(id) && id === CUSTOM_DEPARTMENT_ROW_ID) {
      rowsById.set(id, {
        id,
        departmentId: "",
        name: CUSTOM_DEPARTMENT_ROW_LABEL,
        shortName: "",
        shifts: [],
        employeeIds: new Set(),
        isCustomDepartment: true,
      });
    }
    if (!rowsById.has(id) && includeUnassigned && !String(shift?.department || "").trim()) {
      rowsById.set(id, {
        id,
        departmentId: "",
        name: id,
        shortName: "",
        shifts: [],
        employeeIds: new Set(),
        isUnassigned: true,
      });
    }

    const row = rowsById.get(id);
    if (!row) return;

    row.shifts.push(shift);
    if (shift?.employeeId) row.employeeIds.add(shift.employeeId);
  });

  const filtered = Array.from(rowsById.values()).filter((department) => {
    if (!normalizedQuery) return true;
    if (includesQuery(department.name, normalizedQuery)) return true;

    return department.shifts.some(
      (shift) =>
        includesQuery(shift.title, normalizedQuery) ||
        includesQuery(shift.employeeName, normalizedQuery) ||
        includesQuery(shift.timeLabel, normalizedQuery)
    );
  });

  return filtered
    .sort((left, right) => {
      // Sammelzeilen stehen fest am Ende, unabhängig von der gewählten
      // Reihenfolge: sie sind keine Abteilungen, die jemand sortiert hätte.
      if (departmentRowRank(left) !== departmentRowRank(right)) {
        return departmentRowRank(left) - departmentRowRank(right);
      }

      const leftOrder = orderIndexById.has(left.id) ? orderIndexById.get(left.id) : Number.POSITIVE_INFINITY;
      const rightOrder = orderIndexById.has(right.id) ? orderIndexById.get(right.id) : Number.POSITIVE_INFINITY;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;

      const result = String(left.name || "").localeCompare(String(right.name || ""), "de", {
        sensitivity: "base",
      });
      return sortDirection === "desc" ? -result : result;
    })
    .map((department) => ({
      id: department.id,
      departmentId: department.departmentId,
      name: department.name,
      shortName: department.shortName,
      isUnassigned: Boolean(department.isUnassigned),
      isCustomDepartment: Boolean(department.isCustomDepartment),
      shiftCount: department.shifts.length,
      employeeCount: department.employeeIds.size,
      metaLabel: formatShiftCount(department.shifts.length),
    }));
}

function departmentRowRank(row) {
  if (row?.isCustomDepartment) return 1;
  if (row?.isUnassigned) return 2;
  return 0;
}
